import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';

import LayoutSlice from '~/common/stores/layout.slice.ts';
import AdminSlice from '~/features/admin/stores/admin.slice.ts';
import AuthSlice from '~/features/auth/stores/auth.slice.ts';
import DashboardSlice from '~/features/dashboard/stores/dashboard.slice.ts';
import SettlementSlice from '~/features/dashboard/stores/settlement.slice.ts';
import FactorSlice from '~/features/factor/stores/factor.slice.ts';
import InquirySlice from '~/features/inquiry/stores/inquiry.slice.ts';
import NoticeSlice from '~/features/notice/stores/notice.slice.ts';
import ProductSlice from '~/features/product/stores/product.slice.ts';
import PurchaseSlice from '~/features/purchase/stores/purchase.slice.ts';
import RemotePcAgentSlice from '~/features/remote-pc-agent/stores/remote-pc-agent.slice.ts';
import StoreAnalyticsSlice from '~/features/store-console/stores/store-analytics.slice.ts';
import StoreConsoleSlice from '~/features/store-console/stores/store-console.slice.ts';
import StoreSlice from '~/features/store/stores/store.slice.ts';
import UserAccessHistorySlice from '~/features/user-access-history/stores/user-access-history.slice.ts';
import UserDetailModalSlice from '~/features/user/stores/user-detail-modal.slice.ts';
import UserSlice from '~/features/user/stores/user.slice.ts';
import VendorGameSlice from '~/features/vendor-game/stores/vendor-game.slice.ts';
import VendorSlice from '~/features/vendor/stores/vendor.slice.ts';
import WolAgentSlice from '~/features/wol-agent/stores/wol-agent.slice.ts';

import type { TypedUseSelectorHook } from 'react-redux';

export const ACTION_STORE_CLEAR = { type: 'root/STORE_CLEAR' } as const;

const combined = combineReducers({
  layout: LayoutSlice.reducer,
  userDetailModal: UserDetailModalSlice.reducer,
  auth: AuthSlice.reducer,
  store: StoreSlice.reducer,
  storeAnalytics: StoreAnalyticsSlice.reducer,
  storeConsole: StoreConsoleSlice.reducer,
  remotePcAgent: RemotePcAgentSlice.reducer,
  user: UserSlice.reducer,
  admin: AdminSlice.reducer,
  notice: NoticeSlice.reducer,
  purchase: PurchaseSlice.reducer,
  dashboard: DashboardSlice.reducer,
  settlement: SettlementSlice.reducer,
  product: ProductSlice.reducer,
  vendor: VendorSlice.reducer,
  vendorGame: VendorGameSlice.reducer,
  factor: FactorSlice.reducer,
  inquiry: InquirySlice.reducer,
  userAccessHistory: UserAccessHistorySlice.reducer,
  wolAgent: WolAgentSlice.reducer,
});

const rootReducer: typeof combined = (state, action) => {
  switch (action.type) {
    case ACTION_STORE_CLEAR.type:
      return combined(undefined, action);

    default:
      return combined(state, action);
  }
};

export const store = configureStore({
  devTools: process.env.NODE_ENV === 'development',
  middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }),
  reducer: rootReducer,
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
